// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a form component for managing an organization's branding assets,
 * specifically its logo and icon. The logo is typically a full-width image containing
 * both icon and text, while the icon is a square image used for compact displays.
 */
import { SparseOrganization } from '@/account/api/types';
import { uploadOrganizationIcon } from '@/account/api/upload-organization-icon';
import { uploadOrganizationLogo } from '@/account/api/upload-organization-logo';
import { AssetMarkerSelector } from '@/account/components/asset-marker-selector';
import { useUpdateOrganization } from '@/account/hooks/use-update-organization';
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { FormAvatarInput } from '@/components/form-avatar-input';
import { FormCard } from '@/components/form-card';
import { FormCardRow } from '@/components/form-card-row';
import { SupportSnackbar } from '@/components/support-snackbar';
import { getFileURL } from '@/file/utilities/get-download-url';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating form input data.
 * Both logo and icon are optional and must be File objects when provided.
 */
const formSchema = z.object({
  logo: z.instanceof(File).nullable(),
  icon: z.instanceof(File).nullable(),
  assetMarker: z.string().nullable(),
});

/** Type representing the validated form data */
type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * The organization to update.
   */
  organization: SparseOrganization;
}

/**
 * A form component that allows users to update their organization's branding assets.
 * Provides separate upload areas for the full logo and the square icon.
 *
 * The logo is typically a horizontal image that includes both the brand icon and name,
 * while the icon is a square image used in more compact displays.
 */
export function OrganizationLogoCard(props: Props) {
  const queryClient = useQueryClient();

  const uploadLogoMutation = useMutation({
    mutationFn: uploadOrganizationLogo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({
        queryKey: ['organization', props.organization.key],
      });
      reset({ logo: null, icon: null, assetMarker: watch('assetMarker') });
    },
  });

  const uploadIconMutation = useMutation({
    mutationFn: uploadOrganizationIcon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({
        queryKey: ['organization', props.organization.key],
      });
      reset({ logo: null, icon: null, assetMarker: watch('assetMarker') });
    },
  });

  const updateMarkerMutation = useUpdateOrganization();

  const { control, formState, reset, handleSubmit, setValue, watch } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        logo: null,
        icon: null,
        assetMarker: props.organization.assetMarker || null,
      },
    });

  const isDisabled =
    uploadLogoMutation.isPending ||
    uploadIconMutation.isPending ||
    updateMarkerMutation.isPending ||
    !formState.isDirty;
  // A plan limit refusal is left to the trial-limit toast, so it opens no snackbar here.
  const isError =
    (uploadLogoMutation.isError &&
      !isPlanLimitError(uploadLogoMutation.error)) ||
    (uploadIconMutation.isError &&
      !isPlanLimitError(uploadIconMutation.error)) ||
    updateMarkerMutation.isError;

  /**
   * Handles form submission when the data is valid.
   * Uploads both logo and icon if they have been changed.
   */
  const handleValidSubmission: SubmitHandler<FormData> = async (data) => {
    if (data.logo) {
      uploadLogoMutation.mutate(data.logo);
    }

    if (data.icon) {
      uploadIconMutation.mutate(data.icon);
    }

    if (
      data.assetMarker !== props.organization.assetMarker &&
      data.assetMarker !== null &&
      data.assetMarker !== undefined
    ) {
      updateMarkerMutation.mutate(
        {
          orgKey: props.organization.key,
          parameters: { assetMarker: data.assetMarker },
        },
        {
          onSuccess: (response) => {
            reset({
              logo: null,
              icon: null,
              assetMarker: response.organization.assetMarker || null,
            });
          },
        }
      );
    }
  };

  /**
   * Resets the form to its initial state, discarding any changes.
   */
  const handleReset = () => {
    reset();
  };

  /**
   * Resets any error states from failed uploads.
   */
  const resetMutations = () => {
    uploadLogoMutation.reset();
    uploadIconMutation.reset();
    updateMarkerMutation.reset();
  };

  return (
    <FormCard
      title="Branding"
      isDirty={formState.isDirty}
      isDisabled={isDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <SupportSnackbar
        isOpen={isError}
        onClose={resetMutations}
        message="An error occurred while updating your organization's branding."
      />
      <FormCardRow
        title="Logo"
        subtitle="The full-sized version of your logo, often a horizontal image that includes both your brand icon and brand name"
      >
        <Box
          minWidth={300}
          display="flex"
          marginLeft="auto"
          justifyContent="end"
        >
          <FormAvatarInput
            name="logo"
            control={control}
            defaultSource={getFileURL(props.organization.logoFileKey)}
            sx={{ width: 192, height: 96 }}
          >
            {props.organization.name}
          </FormAvatarInput>
        </Box>
      </FormCardRow>
      <FormCardRow
        title="Icon"
        subtitle="A smaller representation of your brand identity, often a square image that does not include your brand name"
      >
        <Box
          minWidth={300}
          display="flex"
          marginLeft="auto"
          justifyContent="end"
        >
          <FormAvatarInput
            name="icon"
            control={control}
            defaultSource={getFileURL(props.organization.iconFileKey)}
            sx={{ width: 96, height: 96, transform: 'translateX(-50%)' }}
          >
            {props.organization.name.at(0)}
          </FormAvatarInput>
        </Box>
      </FormCardRow>
      <FormCardRow
        title="Marker"
        subtitle="The small symbol to represent your assets throughout the Hardfin platform"
      >
        <Box display="flex" marginLeft="auto" justifyContent="end">
          <AssetMarkerSelector
            selectedMarker={watch('assetMarker')}
            onMarkerSelect={(markerName) =>
              setValue('assetMarker', markerName, { shouldDirty: true })
            }
          />
        </Box>
      </FormCardRow>
    </FormCard>
  );
}
